import { EditorLanguageContext } from '@app/components/cms/context/EditorLanguageContext';
import { Button } from '@app/components/various/Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '@app/components/various/Dropdown';
import { getIsPublishing } from '@app/ducks';
import { getHasUnpublishedChanges, getIsPublished } from '@app/logic/pages';
import { usePagePublicationActions } from '@app/utils/hooks';
import { Cms } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  pageMeta: Cms.Page;
}

export const SingleLanguageButton = ({ pageMeta }: Props) => {
  const { status, lastPublishedDate, id } = pageMeta;

  const { t } = useTranslation(['cmsConfirmationConfig', 'cms', 'common']);
  const { defaultLanguage } = React.useContext(EditorLanguageContext);

  const { secondaryMenuActions, handlePublishPage, handleUnpublishPage } = usePagePublicationActions({
    closeMenu: () => null,
    languages: [defaultLanguage],
    lastPublishedDate,
    pageId: id,
    status,
  });

  const isPublished = getIsPublished(status);
  const isPublishing = useSelector(getIsPublishing);
  const hasUnpublishedChanges = getHasUnpublishedChanges(status);

  if (isPublished && !hasUnpublishedChanges) {
    return (
      <Button color="secondary" variant="bordered" isLoading={isPublishing} onClick={handleUnpublishPage}>
        {t('cmsConfirmationConfig:unpublish_page.okText')}
      </Button>
    );
  }

  const publishButtonName = isPublished ? t('common:update') : t('cms:publish');

  return (
    <div className="flex gap-4">
      <Button isLoading={isPublishing} onClick={handlePublishPage}>
        {publishButtonName}
      </Button>
      {isPublished && (
        <Dropdown title={t('cms:more_publish_options')}>
          <DropdownTrigger>
            {({ isOpen, triggerProps }) => (
              <Button
                color="secondary"
                variant="bordered"
                isChevronOpen={isOpen}
                aria-label={t('cms:more_publish_options')}
                {...triggerProps}
              >
                {t('common:more')}
              </Button>
            )}
          </DropdownTrigger>
          <DropdownMenu items={secondaryMenuActions} />
        </Dropdown>
      )}
    </div>
  );
};
